/**
 * Environment-switched endpoints.
 *
 * Ported from viana-journeys-portal-26/src/config.tsx (branch dev_gcp). The field NAMES are kept
 * verbatim (`base_url`, `api`, `socketUrl`, `broker` …) so the ~96 ported files need no rewriting;
 * three things differ deliberately:
 *
 *   1. The switch variable is VITE_DEMOS_ENV — this repo is not the portal.
 *   2. The Keycloak client id is env-driven, so swapping clients is config, not code.
 *   3. **No secrets.** The portal hard-codes a Google Maps API key AND the MQTT broker
 *      username/password in this file, which puts them in the published bundle and in git.
 *      Here they start empty and are filled at runtime from `GET /journeys/config` on our own
 *      API — see hydrateRuntimeSecrets() below.
 */

export type VianaEnv = 'dev_gcp' | 'stg_gcp' | 'ppd_gcp' | 'prod_gcp';

export interface BrokerConfig {
  url: string;
  path: string;
  port: string;
  protocol: string;
  clientID: string;
  /** Empty until hydrateRuntimeSecrets() runs. */
  username: string;
  /** Empty until hydrateRuntimeSecrets() runs. */
  password: string;
}

export interface Endpoints {
  base_url: string;
  base_url_insights: string;
  base_url_superset: string;
  base_url_supersetv2: string;
  viana_portal_url: string;
  socketUrl: string;
  api: string;
  brainstorm_api: string;
  broker: BrokerConfig;
  /** Empty until hydrateRuntimeSecrets() runs. */
  googlemaps_api_key: string;
}

export interface AuthConfig {
  auth_url: string;
  realm: string;
  client_id: string;
}

const broker = (host: string): BrokerConfig => ({
  url: host,
  path: '/mqtt',
  port: '8084',
  protocol: 'wss',
  clientID: 'viana-demos',
  username: '',
  password: '',
});

const ENVS: Record<VianaEnv, { endpoints: Endpoints; auth: Omit<AuthConfig, 'client_id'> }> = {
  dev_gcp: {
    endpoints: {
      base_url: 'https://dev-gcp.services.api.viana.ai/',
      base_url_insights: 'https://dev-gcp.insights.api.viana.ai/',
      base_url_superset: 'https://dev-gcp.superset.internal.viana.ai',
      base_url_supersetv2: 'https://v2-dev-gcp.superset.internal.viana.ai',
      viana_portal_url: 'https://dev-gcp.portal.internal.viana.ai/',
      socketUrl: 'wss://dev.runtimes-ws.viana.ai',
      api: 'https://dev.runtimes-api.viana.ai',
      brainstorm_api: 'https://api.brainstorm-journeys.viana.ai',
      broker: broker('wss://dev-gcp.broker.internal.viana.ai'),
      googlemaps_api_key: '',
    },
    auth: { auth_url: 'https://dev-gcp.id2.internal.viana.ai/auth', realm: 'staging' },
  },
  stg_gcp: {
    endpoints: {
      base_url: 'https://stg-gcp.services.api.viana.ai/',
      base_url_insights: 'https://viana-insights-210498958095.australia-southeast2.run.app/',
      base_url_superset: 'https://stg-gcp.superset.internal.viana.ai',
      base_url_supersetv2: 'https://v2-dev-gcp.superset.internal.viana.ai',
      viana_portal_url: 'https://stg-gcp.portal.internal.viana.ai/',
      socketUrl: 'wss://staging.runtimes-ws.viana.ai',
      api: 'https://staging.runtimes-api.viana.ai',
      brainstorm_api: 'https://staging.api.brainstorm-journeys.viana.ai',
      broker: broker('wss://staging-gcp.broker.internal.viana.ai'),
      googlemaps_api_key: '',
    },
    auth: { auth_url: 'https://dev-gcp.id2.internal.viana.ai/auth', realm: 'staging' },
  },
  ppd_gcp: {
    endpoints: {
      base_url:
        'https://crn-viana-ppd-ausse2-aiservices-939112729767.australia-southeast2.run.app/',
      base_url_insights:
        'https://crn-viana-ppd-ausse2-aiinsights-939112729767.australia-southeast2.run.app/',
      base_url_superset: 'https://ppd-gcp.superset.internal.viana.ai',
      base_url_supersetv2: 'https://v2-dev-gcp.superset.internal.viana.ai',
      viana_portal_url: 'https://ppd-gcp.portal.internal.viana.ai/',
      socketUrl: 'wss://ppd-gcp.runtimes-ws.viana.ai',
      api: 'https://ppd.runtimes-api.viana.ai',
      brainstorm_api: 'https://ppd.api.brainstorm-journeys.viana.ai',
      broker: broker('wss://ppd-gcp.broker.internal.viana.ai'),
      googlemaps_api_key: '',
    },
    auth: { auth_url: 'https://dev-gcp.id2.internal.viana.ai/auth', realm: 'preprod' },
  },
  prod_gcp: {
    endpoints: {
      base_url: 'https://prod-gcp.services.api.viana.ai/',
      base_url_insights: 'https://prod-gcp.insights.api.viana.ai/',
      base_url_superset: 'https://prod-gcp.superset.internal.viana.ai',
      base_url_supersetv2: 'https://v2-dev-gcp.superset.internal.viana.ai',
      viana_portal_url: 'https://portal.viana.ai/',
      socketUrl: 'wss://prod.runtimes-ws.viana.ai',
      api: 'https://prod.runtimes-api.viana.ai',
      brainstorm_api: 'https://api.brainstorm-journeys.viana.ai',
      broker: broker('wss://prod-gcp.broker.internal.viana.ai'),
      googlemaps_api_key: '',
    },
    auth: { auth_url: 'https://prod-gcp.id2.viana.ai/auth', realm: 'production' },
  },
};

function resolveEnv(): VianaEnv {
  const raw = import.meta.env.VITE_DEMOS_ENV;
  if (raw && raw in ENVS) return raw as VianaEnv;
  if (raw) console.warn(`[config] unknown VITE_DEMOS_ENV "${raw}" — falling back to dev_gcp`);
  return 'dev_gcp';
}

export const VIANA_ENV: VianaEnv = resolveEnv();

export const endpoints: Endpoints = ENVS[VIANA_ENV].endpoints;

export const auth: AuthConfig = {
  ...ENVS[VIANA_ENV].auth,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'viana-demos',
};

/** Base URL of this repo's own BFF (apps/api). */
export const apiBaseUrl: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3100';

export interface RuntimeSecrets {
  googleMapsApiKey?: string | null;
  brokerUsername?: string | null;
  brokerPassword?: string | null;
}

/**
 * Fills in the values that must not ship in the bundle. Called once by a demo before it renders,
 * with the response from our own authenticated API, so the rest of the ported code can keep
 * reading `endpoints.broker.username` synchronously the way it always did.
 */
export function hydrateRuntimeSecrets(secrets: RuntimeSecrets): void {
  if (secrets.googleMapsApiKey) endpoints.googlemaps_api_key = secrets.googleMapsApiKey;
  if (secrets.brokerUsername) endpoints.broker.username = secrets.brokerUsername;
  if (secrets.brokerPassword) endpoints.broker.password = secrets.brokerPassword;
}
